import { useCallback, useMemo, useRef } from 'react';
import { useFormik } from 'formik';
import { type QueryKey, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getErrorMessage } from '@/api/core/axiosErrorMapper';
import { SystemNotification } from '@/app/components/feedback/SystemNotification';
import { type AnnouncementDto } from '@/app/models/AnnouncementDto';
import { type AnnouncementPayload } from '@/app/models/AnnouncementPayload';
import { type AttachmentDto } from '@/app/models/AttachmentDto';
import { ANNOUNCEMENTS_QUERY_KEY } from '@/app/modules/announcements/core/_hooks';
import { getDataTableQueryKey } from '@/app/modules/table/DataTable';
import { MANAGED_ANNOUNCEMENTS_TABLE_KEY } from '@/app/pages/notifications/announcements/core/_consts';
import {
  type AnnouncementActionVariables,
  type AnnouncementFormOptions,
  type AnnouncementFormValues,
  type AnnouncementMediaUploadVariables,
  type AnnouncementSaveVariables,
  type AnnouncementValidityFormValues,
  type AudienceContactsResult,
  getAnnouncementFormValues,
} from '@/app/pages/notifications/announcements/core/_models';
import {
  getDetails,
  add,
  edit,
  listAudienceContacts,
  listAudienceRoles,
  updateValidity,
  uploadMedia,
} from '@/app/pages/notifications/announcements/core/_requests';
import {
  mapAnnouncementToDuplicateValues,
  mapAnnouncementToFormValues,
  mapFormValuesToPayload,
  mapValidityFormValuesToPayload,
  userIdsOf,
} from '@/app/pages/notifications/announcements/core/_utils';
import {
  announcementFormSchema,
  announcementValiditySchema,
} from '@/app/pages/notifications/announcements/core/_validation';
import { toDateTimeLocalInput } from '@/app/pages/notifications/core/_utils';

// Chave própria: as mutações de recebimento (visto/ciente/dispensado) só invalidam ['announcements', ...].
const AUDIENCE_CONTACTS_QUERY_KEY: QueryKey = ['announcement-audience', 'contacts'];
const AUDIENCE_ROLES_QUERY_KEY: QueryKey = ['announcement-audience', 'roles'];
const ANNOUNCEMENT_DETAILS_QUERY_KEY_ROOT: QueryKey = ['announcement-details'];
const PUBLISHED_MESSAGE = 'Comunicado publicado';
export const MANAGED_TABLE_QUERY_KEY: QueryKey = getDataTableQueryKey(MANAGED_ANNOUNCEMENTS_TABLE_KEY);

/** A mesma chave em criar (duplicar) e editar: cada um busca e invalida o rascunho do seu próprio id. */
export const announcementDetailsQueryKey = (id: string | undefined): QueryKey => [
  ...ANNOUNCEMENT_DETAILS_QUERY_KEY_ROOT,
  id,
];

export const useAudienceContacts = () =>
  useQuery({
    queryKey: AUDIENCE_CONTACTS_QUERY_KEY,
    queryFn: async (): Promise<AudienceContactsResult> => {
      const { data } = await listAudienceContacts();
      // Contato sem acesso ativo não recebe comunicado: não vira opção.
      const contacts = data.content.filter((contact) => userIdsOf([contact]).length > 0);
      return { contacts, isTruncated: data.total_elements > data.content.length };
    },
  });

export const useAudienceRoles = () =>
  useQuery({
    queryKey: AUDIENCE_ROLES_QUERY_KEY,
    queryFn: async () => {
      const { data } = await listAudienceRoles();
      return data.content;
    },
  });

export const useAnnouncementDetails = (id: string | undefined, enabled: boolean, handleErrorLocally = false) =>
  useQuery({
    queryKey: announcementDetailsQueryKey(id),
    queryFn: async (): Promise<AnnouncementDto> => {
      const { data } = await getDetails(id ?? '');
      return data;
    },
    enabled: enabled && !!id,
    ...(handleErrorLocally ? { meta: { suppressGlobalError: true }, throwOnError: false } : {}),
  });

export const useUploadAnnouncementMedia = () =>
  useMutation({
    mutationFn: async ({
      file,
      signal,
      onUploadProgress,
    }: AnnouncementMediaUploadVariables): Promise<AttachmentDto> => {
      const response = await uploadMedia(file, signal, onUploadProgress);
      if (response.status !== 200 || !response.data.id || !response.data.public_url) {
        throw new Error('Não foi possível enviar o arquivo.');
      }
      return response.data;
    },
    retry: false,
    gcTime: 0,
    throwOnError: false,
  });

const useRefreshManagedAnnouncements = (): (() => Promise<void>) => {
  const queryClient = useQueryClient();
  return useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: MANAGED_TABLE_QUERY_KEY });
    // Quem publica também pode receber: o sino e os pendentes dele mudam junto.
    await queryClient.invalidateQueries({ queryKey: ANNOUNCEMENTS_QUERY_KEY });
  }, [queryClient]);
};

/** Publicar, encerrar e excluir: uma chamada, um aviso e a lista atualizada. */
export const useAnnouncementAction = () => {
  const refreshManagedAnnouncements = useRefreshManagedAnnouncements();
  return useMutation({
    mutationFn: ({ id, request }: AnnouncementActionVariables) => request(id),
    onSuccess: async (_data, { successMessage }) => {
      SystemNotification.success(successMessage);
      await refreshManagedAnnouncements();
    },
    onError: (error) => {
      const errorMessage = getErrorMessage(error);
      SystemNotification.error(errorMessage);
    },
  });
};

/** Criar e editar: o mesmo formulário, a mesma validação e o mesmo aviso ao salvar ou publicar. */
const useAnnouncementForm = ({ source, toFormValues, save, draftSuccessMessage, onSaved }: AnnouncementFormOptions) => {
  const queryClient = useQueryClient();
  const refreshManagedAnnouncements = useRefreshManagedAnnouncements();
  // O submit do Formik só leva os valores: qual botão foi clicado fica guardado aqui até o onSubmit.
  const shouldPublishRef = useRef(false);

  const saveMutation = useMutation({
    mutationFn: ({ values, shouldPublish }: AnnouncementSaveVariables) => {
      const payload = mapFormValuesToPayload(values);
      return save(payload, shouldPublish);
    },
    onSuccess: async (_data, { shouldPublish }) => {
      const successMessage = shouldPublish ? PUBLISHED_MESSAGE : draftSuccessMessage;
      SystemNotification.success(successMessage);
      if (shouldPublish) {
        await refreshManagedAnnouncements();
      } else {
        await queryClient.invalidateQueries({ queryKey: MANAGED_TABLE_QUERY_KEY });
      }
      onSaved();
    },
    onError: (error) => {
      const errorMessage = getErrorMessage(error);
      SystemNotification.error(errorMessage);
    },
  });

  const initialValues = useMemo(
    () => (source ? toFormValues(source) : getAnnouncementFormValues({})),
    [source, toFormValues],
  );

  const formik = useFormik<AnnouncementFormValues>({
    initialValues,
    enableReinitialize: true,
    validationSchema: announcementFormSchema,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: (values) => saveMutation.mutate({ values, shouldPublish: shouldPublishRef.current }),
  });

  const submit = (shouldPublish: boolean) => {
    shouldPublishRef.current = shouldPublish;
    void formik.submitForm();
  };

  return {
    formik,
    isSaving: saveMutation.isPending,
    isPublishing: saveMutation.isPending && saveMutation.variables.shouldPublish,
    saveDraft: () => submit(false),
    publishNow: () => submit(true),
  };
};

export const useCreateAnnouncementForm = (source: AnnouncementDto | undefined, onSaved: () => void) =>
  useAnnouncementForm({
    source,
    toFormValues: mapAnnouncementToDuplicateValues,
    save: add,
    draftSuccessMessage: 'Rascunho salvo',
    onSaved,
  });

export const useEditAnnouncementForm = (
  announcementId: string | undefined,
  source: AnnouncementDto | undefined,
  onSaved: () => void,
) => {
  const queryClient = useQueryClient();
  const saveAnnouncement = async (payload: AnnouncementPayload, shouldPublish: boolean) => {
    if (!announcementId) return;
    await edit(announcementId, payload, shouldPublish);
  };
  const closeAfterSave = () => {
    const detailsQueryKey = announcementDetailsQueryKey(announcementId);
    void queryClient.invalidateQueries({ queryKey: detailsQueryKey });
    onSaved();
  };
  return useAnnouncementForm({
    source,
    toFormValues: mapAnnouncementToFormValues,
    save: saveAnnouncement,
    draftSuccessMessage: 'Rascunho atualizado',
    onSaved: closeAfterSave,
  });
};

export const useAnnouncementValidityForm = (
  id: string,
  startDate: string,
  endDate: string | undefined,
  onSaved: () => void,
) => {
  const queryClient = useQueryClient();
  const refreshManagedAnnouncements = useRefreshManagedAnnouncements();
  const validityMutation = useMutation({
    mutationFn: (values: AnnouncementValidityFormValues) => {
      const payload = mapValidityFormValuesToPayload(startDate, values.startDate, values.endDate);
      return updateValidity(id, payload);
    },
    onSuccess: async () => {
      SystemNotification.success('Vigência atualizada');
      await refreshManagedAnnouncements();
      const detailsQueryKey = announcementDetailsQueryKey(id);
      await queryClient.invalidateQueries({ queryKey: detailsQueryKey });
      onSaved();
    },
    onError: (error) => {
      const errorMessage = getErrorMessage(error);
      SystemNotification.error(errorMessage);
    },
  });
  const initialStartDate = toDateTimeLocalInput(startDate);
  const storedEndDate = endDate ?? null;
  const initialEndDate = toDateTimeLocalInput(storedEndDate);
  const formik = useFormik<AnnouncementValidityFormValues>({
    initialValues: {
      startDate: initialStartDate,
      endDate: initialEndDate,
    },
    validationSchema: announcementValiditySchema,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: (values) => validityMutation.mutate(values),
  });
  return { formik, isSaving: validityMutation.isPending };
};
